/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useFormContext } from "react-hook-form";
import { When } from "react-if";
import { Description, Heading, Label, Text, theme } from "@nordcloud/gnui";
import { Fieldset, InputField, ContactsInput } from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { showError, showSuccess } from "~/services/toast";
import { AccountContacts, AccountParams } from "../types";

type Props = {
  accountParams: AccountParams;
  additionalSettings: AccountContacts;
  setAdditionalSettings: (contacts: AccountContacts) => void;
};

export function StandardForm({
  accountParams,
  additionalSettings,
  setAdditionalSettings,
}: Props) {
  const {
    register,
    setValue,

    formState: { errors },
  } = useFormContext();

  const [expanded, setExpanded] = React.useState(false);
  return (
    <>
      <Heading level={3} marginBottom="spacing05">
        Account details
      </Heading>
      <Fieldset>
        <Label name="Account Owner" required />
        <ContactsInput
          name="ownerId"
          placeholder="Select account owner"
          value={[additionalSettings.ownerId]}
          status={errors.ownerId && "error"}
          onChange={(id) => {
            try {
              setAdditionalSettings({ ...additionalSettings, ownerId: id });
              setValue("ownerId", id);
              showSuccess(SUCCESS_TEXT.accountOwnerAdded);
            } catch {
              showError(ERROR_TEXT.default);
            }
          }}
        />
        <When condition={errors.ownerId}>
          <Description>Account owner is required.</Description>
        </When>
      </Fieldset>
      <Text
        style={{ cursor: "pointer" }}
        color={theme.color.text.info}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? "- Hide additional settings" : "+ Show additional settings"}
      </Text>
      <When condition={expanded}>
        <InputField
          label="Display Name"
          {...register("displayName")}
          defaultValue={accountParams.displayName}
          error={errors.displayName}
        />
        <InputField
          label="Account Description"
          {...register("description")}
          defaultValue={accountParams.description}
          error={errors.description}
        />
        <Fieldset>
          <Label name="Contact Person" />
          <ContactsInput
            name="contactIds"
            placeholder="Select contact person"
            value={[additionalSettings.contactIds]}
            status={errors.contactIds && "error"}
            onChange={(id) => {
              try {
                setAdditionalSettings({
                  ...additionalSettings,
                  contactIds: id,
                });
                setValue("contactIds", id);
                showSuccess(SUCCESS_TEXT.contactPersonAdded);
              } catch {
                showError(ERROR_TEXT.default);
              }
            }}
          />
        </Fieldset>
      </When>
    </>
  );
}
